import { type JSX, useState } from 'react';

import { cn } from '@change/ds-utils';

import { Text } from '@change/ds-core-ui/next';

import { Translate } from '@change-corgi/core/react/i18n';

// eslint-disable-next-line @change/sibling-module-imports
import { useShareVisualModalBody } from 'src/app/pages/starterDashboardV2/shared/hooks/useTriggerShareVisualModal/useShareVisualModal/components/ShareVisualModalBody/hooks/useShareVisualModalBody';

import { useShareVisual } from '../GrowPetition/components/ShareVisual/hooks/useShareVisual';

import { DownloadedMessage } from './components/DownloadedMessage';
import { Loading } from './components/Loading';
import { SectionChips } from './components/SectionChips';
import { ShareableAsset } from './components/ShareableAsset';
import { Timeout } from './components/Timeout';
import { useSocialMediaContent } from './hooks/useSocialMediaContent';

export function SocialMediaContent(): JSX.Element | null {
	const context = 'starter_dashboard_grow_petition';
	const [activeSection, setActiveSection] = useState<'stories' | 'posts'>('stories');
	const {
		data: { petition, shareVisualDownloaded, shareVisualEnabled, shareVisualsState },
		actions,
	} = useShareVisual();
	const { data } = useShareVisualModalBody({ petition, context });
	const {
		data: { newModuleEnabled },
	} = useSocialMediaContent();

	const stories = data.shareVisuals.filter((item) => item.variantName.startsWith('story'));
	const posts = data.shareVisuals.filter((item) => item.variantName.startsWith('post'));

	if (!newModuleEnabled) return null;

	if (!shareVisualEnabled) return null;

	if (shareVisualsState === 'loading') return <Loading />;

	if (shareVisualsState === 'timeout') return <Timeout />;

	return (
		<section
			data-qa="social-media-content-container"
			data-testid="social-media-content-loaded-container"
			className="bg-background-base flex flex-col gap-0 py-8 pl-4 pr-0 md:p-12 lg:pr-2"
		>
			<div className="mb-5 flex flex-col gap-3">
				<Text
					id="social-media-content-title"
					data-qa="social-media-content-title"
					variant="heading"
					as="h3"
					className="text-typography-strong text-size-heading-xs md:text-size-heading-small"
				>
					<Translate value="corgi.petition.starter_dashboard_v2.social_media_content.title" />
				</Text>
				<Text as="p">
					<Translate value="corgi.petition.starter_dashboard_v2.social_media_content.description" />
				</Text>
			</div>
			<div>
				<SectionChips
					section={activeSection}
					setSection={setActiveSection}
					showStories={stories.length > 0}
					showPosts={posts.length > 0}
				/>
			</div>
			<div className={cn('flex flex-col', { hidden: activeSection !== 'stories' })}>
				<div className="flex snap-x flex-nowrap gap-4 overflow-y-visible overflow-x-scroll pb-0">
					{stories.map((item, index) => (
						<ShareableAsset
							isLast={index === stories.length - 1}
							asset={item}
							key={item.id}
							submit={actions.onShareVisualDownload}
							type="story"
						/>
					))}
				</div>
			</div>
			<div className={cn('flex flex-col', { hidden: activeSection !== 'posts' })}>
				<div className="flex snap-x flex-nowrap gap-4 overflow-y-visible overflow-x-scroll lg:pb-0">
					{posts.map((item, index) => (
						<ShareableAsset
							isLast={index === posts.length - 1}
							asset={item}
							key={item.id}
							submit={actions.onShareVisualDownload}
							type="post"
						/>
					))}
				</div>
			</div>
			{stories.length === 0 && posts.length === 0 && (
				<div className="flex flex-col">
					<div className="flex snap-x flex-nowrap gap-4 overflow-y-visible overflow-x-scroll lg:pb-0">
						{data.shareVisuals.map((item) => (
							<ShareableAsset asset={item} key={item.id} submit={actions.onShareVisualDownload} type="legacy" />
						))}
					</div>
				</div>
			)}
			{shareVisualDownloaded && <DownloadedMessage />}
		</section>
	);
}
